import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './UserApprovalPage.module.css';
import { UsersService, AdminUser, AdminUserStatus } from '../services/users.service';
import { SystemService } from '../services/system.service';
import ConfirmationModal from '../components/common/ConfirmationModal';

type ModalState =
  | { open: false }
  | {
      open: true;
      user: AdminUser;
      loading: boolean;
      error: string | null;
      kyc?: string;
      pld?: string;
    };

export default function UserApprovalPage() {
  const { t } = useTranslation();

  const [enabled, setEnabled] = useState(true);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>({ open: false });
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
    variant?: 'danger' | 'default';
  }>({ open: false, title: '', message: '', action: async () => {} });

  const statusMap: Record<AdminUserStatus, { label: string; className: string }> = useMemo(
    () => ({
      pending: { label: t('admin.status_pending') || 'Pendiente', className: styles.pending },
      review: { label: t('admin.status_review') || 'En revisión', className: styles.review },
      missing_docs: {
        label: t('admin.status_missing_docs') || 'Faltan documentos',
        className: styles.missingDocs
      },
      approved: { label: t('admin.status_approved') || 'Aprobado', className: styles.approved },
      rejected: { label: t('admin.status_rejected') || 'Rechazado', className: styles.rejected }
    }),
    [t]
  );

  const fetchUsers = async (q?: string) => {
    setLoading(true);
    try {
      const res = await UsersService.getPending(q);
      setUsers(Array.isArray(res) ? res : []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;

    const init = async () => {
      try {
        const flag = await SystemService.getFlag('kyc_required');
        if (!alive) return;
        setEnabled(!!flag);
      } catch {
        if (!alive) return;
        setEnabled(true);
      }

      await fetchUsers('');
    };

    init();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (user: AdminUser) => {
    setModal({ open: true, user, loading: true, error: null });
    try {
      const summary = await UsersService.getKycSummary(user.id);
      setModal({
        open: true,
        user,
        loading: false,
        error: null,
        kyc: summary.kycStatus,
        pld: summary.pldStatus
      });
    } catch {
      setModal({
        open: true,
        user,
        loading: false,
        error: t('admin.kyc_summary_error') || 'No se pudo cargar el detalle.'
      });
    }
  };

  const closeDetail = () => setModal({ open: false });

  const executeAction = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch {
      // silent
    } finally {
      setActionLoadingId(null);
      await fetchUsers(query);
    }
  };

  const handleApprove = (user: AdminUser) => {
    setConfirmModal({
      open: true,
      title: 'Aprobar Usuario',
      message: `¿Estás seguro de que deseas aprobar a ${user.name || user.email}? Esta acción activará su cuenta y le permitirá acceder a la plataforma.`,
      action: async () => {
        setActionLoadingId(user.id);
        await executeAction(() => UsersService.approve(user.id));
      },
      variant: 'default'
    });
  };

  const handleReject = (user: AdminUser) => {
    setConfirmModal({
      open: true,
      title: 'Rechazar Usuario',
      message: `¿Estás seguro de que deseas rechazar a ${user.name || user.email}? Esta acción bloqueará permanentemente su cuenta.`,
      action: async () => {
        setActionLoadingId(user.id);
        await executeAction(() => UsersService.reject(user.id));
      },
      variant: 'danger'
    });
  };

  const confirmAction = async () => {
    setConfirmModal(prev => ({ ...prev, open: false }));
    await confirmModal.action();
  };

  const cancelConfirm = () => {
    setConfirmModal(prev => ({ ...prev, open: false }));
  };

  const hasResults = users.length > 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{t('admin.approval_title')}</h1>
          <p className={styles.subtitle}>{t('admin.approval_subtitle')}</p>
        </div>
      </header>

      <section className={styles.card}>
        <div className={styles.cardTop}>
          <div className={styles.cardTitleWrap}>
            <h2 className={styles.cardTitle}>{t('admin.pending')}</h2>
            <span className={styles.cardHint}>
              {enabled
                ? 'Revisa y aprueba usuarios pendientes por motivos de seguridad.'
                : t('admin.kyc_disabled') || 'La validación KYC está deshabilitada por configuración.'}
            </span>
          </div>

          <div className={styles.search}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('admin.search') || 'Buscar…'}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') fetchUsers(query);
                if (e.key === 'Escape') setQuery('');
              }}
              disabled={!enabled}
              aria-label={t('admin.search') || 'Buscar'}
            />
            <button
              type="button"
              className={styles.searchBtn}
              onClick={() => fetchUsers(query)}
              disabled={!enabled}
            >
              {t('common.search') || 'Buscar'}
            </button>
          </div>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('admin.table_user')}</th>
                  <th className={styles.colRole}>{t('admin.table_role')}</th>
                  <th className={styles.colDate}>{t('admin.table_date')}</th>
                  <th className={styles.colStatus}>{t('admin.table_status')}</th>
                  <th className={styles.colActions}>{t('admin.table_actions')}</th>
                </tr>
              </thead>

              <tbody>
                {!enabled ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.empty}>
                        <div className={styles.emptyTitle}>
                          {t('admin.kyc_disabled') || 'KYC deshabilitado'}
                        </div>
                        <div className={styles.emptyText}>
                          {t('admin.kyc_disabled_detail') ||
                            'Activa la validación KYC en configuración del sistema para continuar.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : loading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={`sk-${idx}`} className={styles.skeletonRow}>
                      <td>
                        <div className={styles.skeletonUser}>
                          <div className={styles.skeletonLine} />
                          <div className={`${styles.skeletonLine} ${styles.skeletonLineSmall}`} />
                        </div>
                      </td>
                      <td className={styles.colRole}>
                        <div className={`${styles.skeletonLine} ${styles.skeletonChip}`} />
                      </td>
                      <td className={styles.colDate}>
                        <div className={`${styles.skeletonLine} ${styles.skeletonChip}`} />
                      </td>
                      <td className={styles.colStatus}>
                        <div className={`${styles.skeletonLine} ${styles.skeletonPill}`} />
                      </td>
                      <td className={styles.colActions}>
                        <div className={styles.skeletonActions}>
                          <div className={`${styles.skeletonLine} ${styles.skeletonBtn}`} />
                          <div className={`${styles.skeletonLine} ${styles.skeletonBtn}`} />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : !hasResults ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.empty}>
                        <div className={styles.emptyTitle}>
                          {t('admin.no_results') || 'Sin resultados'}
                        </div>
                        <div className={styles.emptyText}>
                          {t('admin.no_results_hint') || 'No hay usuarios pendientes con este criterio de búsqueda.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id} className={styles.dataRow}>
                      <td>
                        <button
                          type="button"
                          onClick={() => openDetail(user)}
                          className={styles.userBtn}
                          aria-label={`${t('admin.open_detail') || 'Abrir detalle'}: ${user.name}`}
                        >
                          <span className={styles.userName}>{user.name}</span>
                          <span className={styles.userEmail}>{user.email}</span>
                        </button>
                      </td>

                      <td className={styles.colRole}>
                        <span className={styles.roleChip}>{user.role}</span>
                      </td>

                      <td className={styles.colDate}>
                        <span className={styles.mono}>{user.createdAt}</span>
                      </td>

                      <td className={styles.colStatus}>
                        <span className={`${styles.badge} ${statusMap[user.status]?.className || ''}`}>
                          {statusMap[user.status]?.label || user.status}
                        </span>
                      </td>

                      <td className={styles.colActions}>
                        <div className={styles.actions}>
                          <button
                            className={`${styles.btn} ${styles.btnTonal}`}
                            onClick={() => handleApprove(user)}
                            disabled={actionLoadingId === user.id}
                            type="button"
                          >
                            {actionLoadingId === user.id ? 'Procesando...' : (t('admin.approve') || 'Aprobar')}
                          </button>

                          <button
                            className={`${styles.btn} ${styles.btnDanger}`}
                            onClick={() => handleReject(user)}
                            disabled={actionLoadingId === user.id}
                            type="button"
                          >
                            {actionLoadingId === user.id ? 'Procesando...' : (t('admin.reject') || 'Rechazar')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {modal.open ? (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          onMouseDown={e => {
            if (e.target === e.currentTarget) closeDetail();
          }}
        >
          <div className={styles.modal} onMouseDown={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleWrap}>
                <div className={styles.modalTitle}>{modal.user.name}</div>
                <div className={styles.modalSubtitle}>{modal.user.email}</div>
              </div>

              <button type="button" className={styles.modalClose} onClick={closeDetail}>
                {t('common.close') || 'Cerrar'}
              </button>
            </div>

            <div className={styles.modalBody}>
              {modal.loading ? (
                <div className={styles.modalLoading}>{t('common.loading') || 'Cargando...'}</div>
              ) : modal.error ? (
                <div className={styles.modalError}>{modal.error}</div>
              ) : (
                <div className={styles.modalGrid}>
                  <div className={styles.detailCard}>
                    <div className={styles.detailLabel}>KYC</div>
                    <div className={styles.detailValue}>{modal.kyc}</div>
                  </div>

                  <div className={styles.detailCard}>
                    <div className={styles.detailLabel}>PLD</div>
                    <div className={styles.detailValue}>{modal.pld}</div>
                  </div>

                  <div className={styles.detailHint}>
                    {t('admin.detail_hint') || 'Para ver documentos y validar, se integra en HU-36 (KYC/PLD).'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmationModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirmar"
        cancelText="Cancelar"
        variant={confirmModal.variant}
        loading={actionLoadingId !== null}
        onConfirm={confirmAction}
        onCancel={cancelConfirm}
      />
    </div>
  );
}
